import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';

import type { Settings, StateLawsPost, StateLaws, Translation } from 'types';

import camelcaseKeys from 'camelcase-keys';

import { getSettings } from './settings';
import { request } from 'lib/rest';

export type StateLawsListResult = {
  [key: string]: StateLaws;
};

export async function getStateLawsList(
  locale: string,
): Promise<StateLawsListResult> {
  const { error, data } = await request<StateLawsListResult>(`/state-laws`, {
    headers: {
      'Accept-Language': locale,
    },
  });

  if (error) {
    throw error;
  }

  return camelcaseKeys(data.items, { deep: true });
}

export type SingleLawPostProps = {
  post: StateLawsPost;
  settings: Settings;
  isPreview: boolean;
};

async function getSingleStateLawPost({
  locale,
  slug,
  preview,
}: {
  locale: string;
  slug: string;
  preview: boolean;
}): Promise<StateLawsPost> {
  const { error, data } = await request<StateLawsPost>(
    `/state_laws?path=${slug}&_fields=id,title,slug,date,modified,content,seo,acf,state_laws_status,languague,translations,type${
      preview ? '&status=draft' : ''
    }`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }

  return camelcaseKeys(data.items[0], { deep: true });
}

export async function getSingleStateLaw({
  locale,
  params,
  preview,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<SingleLawPostProps>> {
  const slug = params.slug as string;

  try {
    const [stateLaw, settings] = await Promise.all([
      getSingleStateLawPost({ slug, locale, preview }),
      getSettings(locale),
    ]);

    if (!stateLaw || !settings) {
      return {
        notFound: true,
      };
    }
    //generate languageAlternates - hreflang - array
    const hrefLangAnnotations = () => {
      if (stateLaw?.translations?.length > 0) {
        const annotations: Omit<Translation, 'locale'>[] =
          stateLaw?.translations;

        if (
          locale === 'en' ||
          stateLaw?.translations?.find(({ slug }) => slug === 'en')
        ) {
          //hreflang must have x-default
          annotations.push({
            uri:
              locale === 'en'
                ? `/cannabis-marijuana-laws/${slug}`
                : stateLaw?.translations?.find(({ slug }) => slug === 'en')
                    ?.uri,
            slug: 'x-default',
          });
        }
        return annotations;
      }
      return null;
    };

    const languageAlternates = hrefLangAnnotations()
      ? hrefLangAnnotations()?.map(({ slug, uri }) => ({
          hrefLang: slug,
          href: uri ? `https://leafwell.com${uri}` : null,
        }))
      : null;

    return {
      props: {
        post: stateLaw,
        settings: {
          ...settings,
          pageTranslations: stateLaw.translations,
          languageAlternates,
        },
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`Error getting state law ${slug}:`, error);

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}
